import React from 'react';
import { Ruler, Info, ArrowRightLeft, Maximize } from 'lucide-react';
import { Separator } from './ui/separator';

export function SizeGuidePage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-brand-green text-white py-16 px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center mb-6 animate-in fade-in zoom-in duration-700">
            <Ruler className="h-16 w-16 text-brand-gold" />
          </div>
          <h1 className="text-4xl font-serif mb-4 animate-in fade-in slide-in-from-bottom-4 duration-700">
            Find Your <span className="text-brand-gold">Perfect Fit</span>
          </h1>
          <p className="text-brand-gold/80 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
            A guide to measuring and choosing the right size for your jewellery.
          </p>
        </div>
      </section>

      {/* Ring Size Section */}
      <section className="py-20 px-6 max-w-7xl mx-auto">
        <div className="flex items-center space-x-3 mb-8">
          <div className="bg-brand-green p-2 rounded-lg">
            <Maximize className="h-6 w-6 text-white" />
          </div>
          <h2 className="text-3xl font-serif text-brand-green">Ring Size Chart</h2>
        </div>
        
        <div className="overflow-x-auto rounded-xl border border-border shadow-sm mb-12">
          <table className="w-full text-left border-collapse">
            <thead className="bg-brand-green/5 text-brand-green uppercase text-xs tracking-widest font-bold">
              <tr>
                <th className="p-4 border-b">Indian Size</th>
                <th className="p-4 border-b">Inner Diameter (mm)</th>
                <th className="p-4 border-b">Inner Circumference (mm)</th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-border">
              {[
                { size: "6", dia: "14.6", circ: "45.9" },
                { size: "8", dia: "15.3", circ: "48.1" },
                { size: "10", dia: "15.9", circ: "49.9" },
                { size: "12", dia: "16.6", circ: "52.1" },
                { size: "14", dia: "17.2", circ: "54.0" },
                { size: "16", dia: "17.8", circ: "55.9" },
                { size: "18", dia: "18.5", circ: "58.1" },
                { size: "20", dia: "19.1", circ: "60.0" },
              ].map((row, i) => (
                <tr key={i} className="hover:bg-brand-green/5 transition-colors">
                  <td className="p-4 font-medium">{row.size}</td>
                  <td className="p-4 text-muted-foreground">{row.dia} mm</td>
                  <td className="p-4 text-muted-foreground">{row.circ} mm</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* How to Measure Rings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-off-white p-8 rounded-2xl border border-border">
          <div>
            <h3 className="text-xl font-serif text-brand-green mb-4">How to Measure at Home</h3>
            <ol className="space-y-4 text-sm text-muted-foreground list-decimal pl-4">
              <li>Wrap a string or paper around the base of your finger.</li>
              <li>Mark the point where the ends meet.</li>
              <li>Measure the string or paper with a ruler (mm).</li>
              <li>Use the chart above to find your Indian ring size.</li>
            </ol>
          </div>
          <div className="flex items-center justify-center">
            <div className="bg-white p-6 rounded-xl shadow-inner border border-dashed border-brand-green/30 text-center">
              <Info className="h-8 w-8 text-brand-gold mx-auto mb-2" />
              <p className="text-sm italic">Tip: Measure your finger at the end of the day when it's largest.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bangle Size Section */}
      <section className="py-20 px-6 max-w-7xl mx-auto bg-brand-green/5 rounded-3xl">
        <h2 className="text-3xl font-serif text-brand-green mb-8 text-center">Bangle Size Guide</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { size: "2-2", dia: "54.0 mm" },
            { size: "2-4", dia: "57.2 mm" },
            { size: "2-6", dia: "60.3 mm" },
            { size: "2-8", dia: "63.5 mm" },
            { size: "2-10", dia: "66.7 mm" },
            { size: "3-0", dia: "76.2 mm" },
          ].map((b, i) => (
            <div key={i} className="bg-white p-6 rounded-xl shadow-sm text-center border border-brand-green/10">
              <p className="text-brand-green font-bold text-xl">{b.size}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-widest mt-1">Inner Diameter</p>
              <Separator className="my-3" />
              <p className="text-brand-gold font-medium">{b.dia}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Necklace Length Section */}
      <section className="py-20 px-6 max-w-7xl mx-auto text-center">
        <h2 className="text-3xl font-serif text-brand-green mb-12">Necklace Length Visualization</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {[
            { length: "14-16\"", name: "Choker", desc: "Sits high at the neck base" },
            { length: "18\"", name: "Princess", desc: "The most common length" },
            { length: "20-24\"", name: "Matinee", desc: "Hits at the top of cleavage" },
            { length: "30\"+", name: "Opera", desc: "Long and elegant statement" },
          ].map((n, i) => (
            <div key={i} className="group">
              <div className="h-32 flex items-center justify-center bg-off-white rounded-t-2xl border-x border-t border-border group-hover:bg-brand-green/5 transition-colors">
                <ArrowRightLeft className="h-8 w-8 text-brand-green opacity-20 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="p-6 bg-white rounded-b-2xl border border-border shadow-sm">
                <p className="text-brand-green font-serif text-lg">{n.name}</p>
                <p className="text-brand-gold font-bold text-sm mb-2">{n.length}</p>
                <p className="text-xs text-muted-foreground">{n.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
